import { useTranslate } from "@tolgee/react";
import { CircleCheckmark } from "@frontend/lyng/assets/icons/24/outline";
import { Checkmark } from "@frontend/lyng/assets/icons/24/outline";
import { ChevronRight } from "@frontend/lyng/assets/icons/24/outline";
import { useCareContext } from "../../providers";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { ActivityStatus } from "../../api/generated/graphql";
import Berries from "../../assets/berries.svg?react";
import Leaves from "../../assets/leaves.svg?react";
import { Link } from "react-router-dom";
import { Activity } from "../../types";
import { useCallback } from "react";
import { Paragraph } from "@frontend/lyng/typography";

type Props = {
  careRecipients: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    activities?: {
      id: string;
      status: ActivityStatus | null;
    }[];
  }[];
  shiftId: string;
  activities?: Activity[];
};

type CareRecipientCardProps = {
  name: string;
  activitiesLeft: number;
  activitiesMarkedNotCompleted: number;
  shiftId: string;
  id: string;
};

const NoCareRecipients = () => {
  const { t } = useTranslate();

  return (
    <div className="flex grow flex-col gap-8 px-2 pt-2 items-center">
      <div className="flex flex-row items-center justify-between w-2/3 px-8">
        <Berries className="h-16 w-16" />
        <Leaves className="h-16 w-16" />
      </div>
      <Paragraph size="m">
        {t("shiftDetails.careRecipients.noCareRecipients")}
      </Paragraph>
    </div>
  );
};

const CareRecipientCard = ({
  name,
  activitiesLeft,
  activitiesMarkedNotCompleted,
  shiftId,
  id,
}: CareRecipientCardProps) => {
  const { t } = useTranslate();
  const message =
    activitiesLeft > 0
      ? t("shiftDetails.careRecipients.activitiesLeft", {
          count: activitiesLeft,
        })
      : t("shiftDetails.careRecipients.activitiesCompleted");

  const markedNotCompletedMessage = t(
    "shiftDetails.careRecipients.markedNotCompleted",
    { count: activitiesMarkedNotCompleted },
  );
  const isCompleted = activitiesLeft === 0;

  return (
    <Link to={`/shift/${shiftId}/carerecipient/${id}`}>
      <div className="flex items-center justify-between border-b border-greyscale-700 pt-5 pb-4">
        <div className="flex flex-col pl-5 pr-4">
          <Paragraph size="m">{name}</Paragraph>
          <div className="flex items-center mt-1">
            {isCompleted ? (
              <Checkmark className="w-4 h-4 mr-2 text-secondary-500" />
            ) : (
              <CircleCheckmark className="w-4 h-4 mr-2 text-greyscale-400" />
            )}
            <Paragraph size="xs" type={isCompleted ? "tertiary" : "primary"}>
              {message}
            </Paragraph>
            {activitiesMarkedNotCompleted > 0 && !isCompleted && (
              <>
                <span className="text-critical-400 mx-3">•</span>
                <Paragraph
                  size="xs"
                  type="critical"
                  className="text-sm text-critical-400"
                >
                  {markedNotCompletedMessage}
                </Paragraph>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-greyscale-500" />
      </div>
    </Link>
  );
};

export const CareRecipientList = ({
  careRecipients,
  activities,
  shiftId,
}: Props) => {
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  const activitiesLeftForRecipient = useCallback(
    (recipient: { id: string }) => {
      return activities?.filter(
        (activity) =>
          activity.status === ActivityStatus.NotStarted &&
          activity.subject.id === recipient.id,
      ).length;
    },
    [activities],
  );

  const markedNotCompletedForRecipient = useCallback(
    (recipient: { id: string }) => {
      return activities?.filter(
        (activity) =>
          activity.status === ActivityStatus.CouldNotComplete &&
          activity.subject.id === recipient.id,
      ).length;
    },
    [activities],
  );

  if (careRecipients.length === 0) {
    return (
      <div className="p-6">
        <NoCareRecipients />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-2 py-5">
      {careRecipients.map((recipient) => {
        return (
          <CareRecipientCard
            key={recipient.id}
            name={nameOrderFn(recipient)}
            activitiesLeft={activitiesLeftForRecipient(recipient) ?? 0}
            activitiesMarkedNotCompleted={
              markedNotCompletedForRecipient(recipient) ?? 0
            }
            shiftId={shiftId}
            id={recipient.id}
          />
        );
      })}
    </div>
  );
};
